import produce from "immer";
import {
  CellEntity,
  MobileRowLayout,
} from "../components/organisms/ColumnTemplatePositionEditor/types";

type Cell = "EMPTY" | "OVERFLOWN" | CellEntity;
type Blueprint = Cell[][];
type Coordinates = { x: number; y: number };

export const createEmptyMobileLayoutBlueprint = (
  width: number,
  height: number
): Blueprint => {
  const createEmptyRow = (): Cell[] => Array(width).fill("EMPTY");

  return Array(height).fill("").map(createEmptyRow);
};

export const getOverflowCoords = (entity: CellEntity): Coordinates[] => {
  const overflowCoords: Coordinates[] = [];

  for (let { x } = entity; x < entity.x + entity.colspan; x += 1) {
    for (let { y } = entity; y < entity.y + entity.rowspan; y += 1) {
      if (x !== entity.x || y !== entity.y) overflowCoords.push({ x, y });
    }
  }

  return overflowCoords;
};

const throwOverflowError = (): void => {
  throw new Error("Tried adding overflowing entity");
};

const throwOverlapError = (): void => {
  throw new Error("Tried adding two overlapping entities");
};

export const addEntityToBlueprint = (
  blueprint: Blueprint,
  entity: CellEntity
): Blueprint =>
  produce(blueprint, (draft) => {
    const convertCell = (coords: Coordinates, value: Cell): void => {
      const { x, y } = coords;
      if (!draft[y] || !draft[y][x]) throwOverflowError();
      if (draft[y][x] !== "EMPTY") throwOverlapError();
      draft[y][x] = value;
    };

    convertCell({ x: entity.x, y: entity.y }, entity);

    const overflowCoords = getOverflowCoords(entity);
    overflowCoords.forEach((coord) => convertCell(coord, "OVERFLOWN"));
  });

export const generateLayoutBlueprint = ({
  width,
  height,
  cellEntities,
}: MobileRowLayout): Blueprint => {
  const emptyBlueprint = createEmptyMobileLayoutBlueprint(width, height);

  return cellEntities.reduce(
    (acc, curr) => addEntityToBlueprint(acc, curr),
    emptyBlueprint
  );
};

export const removeOverflownFromBlueprint = (
  blueprint: Blueprint
): Exclude<Cell, "OVERFLOWN">[][] => {
  const isNotOverflown = (cell: Cell): cell is Exclude<Cell, "OVERFLOWN"> =>
    cell !== "OVERFLOWN";

  return blueprint.map((row) => row.filter(isNotOverflown));
};

export type LayoutBlueprintCell = Cell;
export type LayoutBlueprint = Blueprint;
export type { Coordinates };
